import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Loader2, AlertCircle, Building2 } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../hooks/useAuth';

/**
 * ADR-018 — criação de launch_deal.
 *
 * Ajuste 3 Gemini: botão "Salvar" desabilitado enquanto authLoading || !user?.id
 *   (evita INSERT com owner=undefined). Guard duplo no handleSubmit.
 */
const STAGE_FALLBACK = ['novo','em_qualificacao','proposta_enviada','em_negociacao','contrato_assinado','encerrado_ganho','encerrado_perdido'];

function fmtBRL(value) {
  const n = Number(value);
  if (!isFinite(n)) return '';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function parseValor(input) {
  if (input === '' || input == null) return null;
  const cleaned = String(input).replace(/[^\d.,-]/g, '').replace(/\./g, '').replace(',', '.');
  const n = Number(cleaned);
  return isFinite(n) ? n : null;
}

export default function LaunchDealCreateModal({ isOpen, onClose, onCreated, defaultEmpresaId = null }) {
  const { user, loading: authLoading } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [stages, setStages] = useState(STAGE_FALLBACK.map((nome) => ({ nome })));
  const [launchesUnitId, setLaunchesUnitId] = useState(null);

  const [empresaId, setEmpresaId] = useState(defaultEmpresaId || '');
  const [empresaSearch, setEmpresaSearch] = useState('');
  const [empreendimentoId, setEmpreendimentoId] = useState('');
  const [empreendimentoSearch, setEmpreendimentoSearch] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [stage, setStage] = useState('novo');
  const [valorEstimado, setValorEstimado] = useState('');
  const [nextStep, setNextStep] = useState('');
  const [nextStepDueDate, setNextStepDueDate] = useState('');
  const [source, setSource] = useState('manual');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      const buR = await supabase.from('business_units').select('id').eq('code', 'launches').single();
      if (!cancelled && buR.data?.id) {
        setLaunchesUnitId(buR.data.id);
        const stR = await supabase
          .from('deal_stages')
          .select('id, nome')
          .eq('business_unit_id', buR.data.id)
          .order('id');
        if (!cancelled && Array.isArray(stR.data) && stR.data.length > 0) {
          setStages(stR.data);
        }
      }
      const empR = await supabase
        .from('empresas')
        .select('id, empresa, company_name, papel_negocio')
        .in('papel_negocio', ['buyer','compradora'])
        .order('empresa');
      if (!cancelled) setEmpresas(empR.data || []);

      const empreR = await supabase
        .from('empreendimentos')
        .select('id, name, short_name, neighborhood, city, phase')
        .order('name');
      if (!cancelled) setEmpreendimentos(empreR.data || []);
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (defaultEmpresaId) setEmpresaId(defaultEmpresaId);
  }, [defaultEmpresaId]);

  const filteredEmpresas = useMemo(() => {
    if (!empresaSearch.trim()) return empresas.slice(0, 50);
    const q = empresaSearch.trim().toLowerCase();
    return empresas
      .filter((e) => (e.empresa || e.company_name || '').toLowerCase().includes(q))
      .slice(0, 50);
  }, [empresaSearch, empresas]);

  const filteredEmpreendimentos = useMemo(() => {
    if (!empreendimentoSearch.trim()) return empreendimentos.slice(0, 50);
    const q = empreendimentoSearch.trim().toLowerCase();
    return empreendimentos
      .filter((e) =>
        (e.name || '').toLowerCase().includes(q) ||
        (e.short_name || '').toLowerCase().includes(q) ||
        (e.neighborhood || '').toLowerCase().includes(q)
      )
      .slice(0, 50);
  }, [empreendimentoSearch, empreendimentos]);

  const formValid = !!title.trim() && (!!empresaId || !!empreendimentoId);

  const handleSubmit = useCallback(async () => {
    if (authLoading || !user?.id) {
      setError('Sessão não confirmada. Recarregue a página.');
      return;
    }
    if (!formValid) {
      setError('Preencha o título e vincule a uma empresa ou empreendimento.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        empresa_id: empresaId || null,
        empreendimento_id: empreendimentoId || null,
        title: title.trim(),
        description: description.trim() || null,
        stage,
        valor_estimado: parseValor(valorEstimado),
        next_step: nextStep.trim() || null,
        next_step_due_date: nextStepDueDate || null,
        source: source || 'manual',
        owner: user.id,
        created_by: user.id,
        updated_by: user.id,
      };
      const { data, error: err } = await supabase
        .from('launch_deals')
        .insert(payload)
        .select('id, title, stage, status, empresa:empresas(id, empresa, company_name)')
        .single();
      if (err) throw err;
      onCreated?.(data);
      onClose();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSubmitting(false);
    }
  }, [authLoading, user, formValid, empresaId, empreendimentoId, title, description, stage, valorEstimado, nextStep, nextStepDueDate, source, onCreated, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[10005] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 size={18} className="text-violet-600" />
            <h2 className="text-base font-bold text-slate-800">Novo deal — Empreendimentos</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" title="Fechar">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {error && (
            <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800 flex items-start gap-1.5">
              <AlertCircle size={12} className="flex-shrink-0 mt-0.5" /> {error}
            </div>
          )}

          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">
              Empresa <span className="text-slate-400 font-normal">(opcional se houver empreendimento)</span>
            </label>
            <input
              type="text"
              placeholder="Buscar incorporadora..."
              value={empresaSearch}
              onChange={(e) => setEmpresaSearch(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-violet-500"
              disabled={!!defaultEmpresaId}
            />
            {!defaultEmpresaId && (
              <select
                value={empresaId}
                onChange={(e) => setEmpresaId(e.target.value)}
                className="mt-1 w-full px-2 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-violet-500 bg-white"
                size={Math.min(6, Math.max(2, filteredEmpresas.length))}
              >
                <option value="">— nenhuma —</option>
                {filteredEmpresas.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.empresa || e.company_name || '(sem nome)'}
                  </option>
                ))}
              </select>
            )}
            {defaultEmpresaId && (
              <div className="text-[11px] text-slate-500 mt-1">Empresa pré-selecionada (vinculada via tab)</div>
            )}
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">
              Empreendimento <span className="text-slate-400 font-normal">(opcional se houver empresa)</span>
            </label>
            <input
              type="text"
              placeholder="Buscar por nome, short_name ou bairro..."
              value={empreendimentoSearch}
              onChange={(e) => setEmpreendimentoSearch(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
            <select
              value={empreendimentoId}
              onChange={(e) => setEmpreendimentoId(e.target.value)}
              className="mt-1 w-full px-2 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-violet-500 bg-white"
              size={Math.min(6, Math.max(2, filteredEmpreendimentos.length))}
            >
              <option value="">— nenhum —</option>
              {filteredEmpreendimentos.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name || e.short_name || '(sem nome)'}
                  {e.neighborhood ? ` — ${e.neighborhood}` : ''}
                  {e.city ? `, ${e.city}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Título *</label>
            <input
              type="text"
              placeholder="Ex: AAM — primeiro contato comercial"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Descrição</label>
            <textarea
              rows={3}
              placeholder="Contexto do deal (opcional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Stage</label>
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-violet-500 bg-white"
              >
                {stages.map((s) => (
                  <option key={s.nome} value={s.nome}>{s.nome}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Source</label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-violet-500 bg-white"
              >
                <option value="manual">manual</option>
                <option value="orulo">orulo</option>
                <option value="referral">referral</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Valor estimado (R$)</label>
              <input
                type="text"
                placeholder="Ex: 150000"
                value={valorEstimado}
                onChange={(e) => setValorEstimado(e.target.value)}
                onBlur={() => {
                  const n = parseValor(valorEstimado);
                  if (n != null) setValorEstimado(fmtBRL(n));
                }}
                className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Próximo passo — vencimento</label>
              <input
                type="date"
                value={nextStepDueDate}
                onChange={(e) => setNextStepDueDate(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-violet-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Próximo passo</label>
            <input
              type="text"
              placeholder="Ex: marcar call com time comercial"
              value={nextStep}
              onChange={(e) => setNextStep(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
          </div>
          {launchesUnitId == null && (
            <div className="text-[11px] text-slate-400 italic">Carregando catálogo de stages...</div>
          )}
        </div>

        <div className="p-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={authLoading || !user?.id || submitting || !formValid || !launchesUnitId}
            className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 hover:bg-violet-700"
          >
            {submitting && <Loader2 size={12} className="animate-spin" />}
            {authLoading ? 'Carregando sessão...' : (submitting ? 'Salvando...' : 'Criar deal')}
          </button>
        </div>
      </div>
    </div>
  );
}
